import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowLeft, Calendar, Clock, ArrowRight, User, Tag, Share2 } from './Icons';
import Footer from './Footer';

// Blog Post Data
const blogPosts = [
  {
    id: 7,
    title: "From Pen to Pixel: GradeMind Now Supports Handwritten Submissions",
    excerpt: "We've bridged the gap between physical assignments and digital grading. Discover how our new handwriting recognition technology enables seamless grading for math, science, and in-class exams.",
    date: "February 15, 2026",
    readTime: "4 min read",
    category: "Product Update",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1517842645767-c639042777db?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">For years, the "last mile" problem in ed-tech has been the physical piece of paper. While English essays moved to Google Docs, math problems, chemical equations, and quick in-class quizzes stayed on the page. Today, we're changing that.</p>
        
        <h3>The Challenge of Analog</h3>
        <p>Teachers in STEM fields know that typing out a calculus proof or drawing a molecular structure in a digital editor is often more difficult than solving the problem itself. This friction has kept millions of assignments offline, trapped in the slow, manual grading loop.</p>
        
        <h3>Enter Multimodal AI</h3>
        <p>GradeMind's latest update leverages state-of-the-art <strong>multimodal AI models</strong>. Unlike traditional OCR (Optical Character Recognition) which simply converts an image to text, our system "sees" the document like a human does.</p>
        <p>It understands:</p>
        <ul>
          <li><strong>Spatial Relationships:</strong> It knows that a numerator sits above a denominator.</li>
          <li><strong>Diagrams:</strong> It can interpret a free-body diagram in Physics or a cell structure in Biology.</li>
          <li><strong>Messy Handwriting:</strong> Trained on diverse datasets, it can decipher cursive, print, and the hurried scrawl of a student rushing to finish an exam.</li>
        </ul>

        <h3>How It Works</h3>
        <ol>
          <li><strong>Scan or Snap:</strong> Students (or teachers) take a photo or scan their handwritten work.</li>
          <li><strong>Upload:</strong> Upload the images (PDF, JPG, PNG) directly to a GradeMind assignment.</li>
          <li><strong>Analyze:</strong> Our AI processes the visual data, mapping handwriting to your rubric criteria just as it would with typed text.</li>
        </ol>

        <h3>Why This Matters</h3>
        <p>This feature unlocks automated grading for:
        <br/>• <strong>Math & Physics:</strong> Where showing work is essential.
        <br/>• <strong>Chemistry:</strong> For balancing equations and drawing structures.
        <br/>• <strong>In-Class Exams:</strong> allowing teachers to keep the security of a pen-and-paper test with the speed of AI grading.</p>
        
        <p>We are excited to see how this features gives time back to STEM educators who have been underserved by text-only tools.</p>
      </>
    )
  },
  {
    id: 1,
    title: "The Future of AI in Education: Beyond Automated Grading",
    excerpt: "Exploring how artificial intelligence is transforming not just grading, but the entire educational experience for students and educators alike.",
    date: "December 15, 2024",
    readTime: "5 min read",
    category: "AI & Education",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">We are standing at the precipice of a shift as significant as the introduction of the calculator or the internet. AI in education is often reduced to "cheating" or "grading," but the reality is far more transformative.</p>

        <h3>The Shift from Grader to Mentor</h3>
        <p>The average educator spends 10-15 hours a week grading. That is 10-15 hours <em>not</em> spent mentoring students, planning engaging lessons, or resting to prevent burnout. By automating the mechanical aspects of evaluation, AI doesn't replace the teacher; it liberates them to be human.</p>

        <h3>Personalized Learning Loops</h3>
        <p>Imagine a classroom where feedback isn't returned two weeks after the test, but seconds after submission. Immediate feedback loops allow students to correct misconceptions instantly, rather than cementing them over weeks of waiting.</p>
        <p>Future iterations of AI tools will not just grade, but suggest personalized remediation paths. "It looks like you're struggling with quadratic factoring. Here is a 5-minute video and three practice problems specifically for this concept."</p>

        <h3>The Equity Engine</h3>
        <p>One of the most promising aspects of AI is its potential to standardize evaluation. While human bias is inevitable—tired teachers grade differently than fresh ones, and implicit biases exist—AI can be calibrated to be radically consistent. It judges the work, not the student's reputation.</p>
        
        <p>We are just getting started. The classroom of 2030 will look very different, not because robots are teaching, but because teachers are finally armed with the tools to teach effectively at scale.</p>
      </>
    )
  },
  {
    id: 2,
    title: "How GradeMind Saves Educators 10+ Hours Per Week",
    excerpt: "A deep dive into the time-saving mechanisms of AI-powered grading and how educators are reclaiming their weekends.",
    date: "December 10, 2024",
    readTime: "4 min read",
    category: "Case Studies",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">Time is the scarcest resource in education. We broke down the workflows of 500 GradeMind users to understand exactly where the time goes and how it's reclaimed.</p>

        <h3>The Anatomy of Grading Time</h3>
        <p>Grading a single essay typically involves:</p>
        <ul>
          <li><strong>Reading (3-5 mins):</strong> comprehending the student's argument.</li>
          <li><strong>Evaluating (2 mins):</strong> Checking against the rubric.</li>
          <li><strong>Commenting (3-5 mins):</strong> Writing constructive feedback.</li>
          <li><strong>Administrative (1 min):</strong> Entering grades into the LMS.</li>
        </ul>
        <p>For a class of 30 students, that's 5+ hours for one assignment. With 5 classes, it's an impossible workload.</p>

        <h3>The GradeMind Multiplier</h3>
        <p>With AI assistance, the workflow shifts:</p>
        <ul>
          <li><strong>AI Analysis (Seconds):</strong> GradeMind reads and generates a draft grade + feedback.</li>
          <li><strong>Human Review (1-2 mins):</strong> The teacher verifies the AI's output, tweaks the tone, and approves it.</li>
        </ul>
        <p><strong>Result:</strong> Grading time per student drops from 10 minutes to 2 minutes. That is an 80% reduction.</p>

        <h3>Real World Impact</h3>
        <p>One of our users, a History teacher in Ohio, reported: "I used to dread Sundays. It was 'grading jail.' Now, I finish my grading during my planning period on Friday. My weekends are mine again."</p>
        <p>It's not about speed for speed's sake. It's about sustainability. Happy, rested teachers stay in the profession longer and inspire their students more.</p>
      </>
    )
  },
  {
    id: 3,
    title: "Maintaining Fairness and Consistency in AI Grading",
    excerpt: "How we ensure every student receives fair, unbiased evaluation regardless of when their assignment is graded or who grades it.",
    date: "December 5, 2024",
    readTime: "6 min read",
    category: "Technology",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1555421689-492a18d9c3ad?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">"Is it fair?" This is the most common question we get. The answer lies in how we design our systems to mitigate bias and ensure consistency.</p>

        <h3>The Problem of Human Drift</h3>
        <p>Human grading suffers from "drift." The first paper you grade at 9 AM often gets a different level of scrutiny than the 50th paper you grade at 11 PM. We are biologically incapable of maintaining identical criteria across hundreds of iterations.</p>
        <p>We also suffer from the "Halo Effect"—giving a good student the benefit of the doubt—and "Horn Effect"—penalizing a disruptive student more harshly.</p>

        <h3>The AI Advantage: Radical Consistency</h3>
        <p>GradeMind's AI models treat every submission as an isolated event. It compares the work strictly against the provided rubric. It does not know if the student is the class valedictorian or the class clown. It does not get tired. It does not get hangry.</p>

        <h3>The Role of the Human in the Loop</h3>
        <p>However, AI is not perfect. It can miss nuance or misinterpret creative leaps. That is why GradeMind is designed as an <strong>assistant, not a replacement</strong>.</p>
        <p>We mandate a review process. You are the pilot; the AI is the autopilot. You must be in the cockpit. By combining the consistency of AI with the judgment of a human educator, we achieve a level of fairness that neither could achieve alone.</p>
      </>
    )
  },
  {
    id: 4,
    title: "Best Practices for Creating Effective Rubrics",
    excerpt: "Learn how to design rubrics that work seamlessly with AI grading while maintaining pedagogical integrity.",
    date: "November 28, 2024",
    readTime: "7 min read",
    category: "Best Practices",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">In the world of AI grading, the old adage "Garbage In, Garbage Out" holds true. The quality of the AI's feedback is directly proportional to the clarity of your rubric.</p>

        <h3>1. Be Specific, Not Subjective</h3>
        <p><strong>Bad:</strong> "Essay has a good flow. (10 pts)"<br/>
        <strong>Good:</strong> "Essay uses transition words between paragraphs and maintains a logical progression from premise to conclusion. (10 pts)"</p>
        <p>AI struggles with "vibes." It excels with criteria.</p>

        <h3>2. Quantify When Possible</h3>
        <p>Instead of "Uses evidence," try "Cites at least three distinct primary sources to support the main argument." This gives the AI a verifiable checklist.</p>

        <h3>3. Define the Tiers</h3>
        <p>Don't just define what an "A" looks like. Define what a "C" looks like.
        <br/>• <strong>Excellent (10pts):</strong> Argument is original, nuanced, and fully supported.
        <br/>• <strong>Competent (7pts):</strong> Argument is present but derivative or relies on weak evidence.
        <br/>• <strong>Needs Improvement (4pts):</strong> Argument is unclear or missing.</p>

        <h3>4. Provide a "Gold Standard" Solution</h3>
        <p>For complex assignments, uploading a sample "A" paper or a model answer helps the AI calibrate its expectations. It serves as a ground-truth anchor for the evaluation.</p>
      </>
    )
  },
  {
    id: 6,
    title: "Understanding Context-Aware Grading Technology",
    excerpt: "How our AI goes beyond keyword matching to truly understand student responses and evaluate critical thinking.",
    date: "November 15, 2024",
    readTime: "8 min read",
    category: "Technology",
    author: "GradeMind Team",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=1000",
    content: (
      <>
        <p className="lead">Early automated grading was simple: Keyword Matching. If the student wrote "George Washington" and "1776," they got the points. If they wrote "The first President" and "Declaration of Independence," they might score zero. It was brittle and easily gamed.</p>

        <h3>Semantic Understanding</h3>
        <p>GradeMind uses Large Language Models (LLMs) built on "Transformers" architecture. This allows the system to understand <strong>semantics</strong>—the meaning behind the words.</p>
        <p>It understands that "The economy collapsed" and "Financial markets plummeted" mean roughly the same thing in context. It evaluates the <em>argument</em>, not just the vocabulary.</p>

        <h3>The Context Window</h3>
        <p>Our models have a massive context window. This means they can hold the entire assignment prompt, the full rubric, the solution key, and the student's submission in "memory" simultaneously.</p>
        <p>This allows for <strong>Cross-Referencing:</strong></p>
        <ul>
          <li>"Did the student answer Part B using the data they calculated in Part A?"</li>
          <li>"Is the conclusion consistent with the evidence presented in the second paragraph?"</li>
        </ul>
        
        <h3>Hallucination Guardrails</h3>
        <p>We employ a technique called "Grounding." We instruct the model to only award points based on evidence found in the document. We also run consistency checks—asking the model to grade the same paper multiple times to ensure the score is stable. If variance is detected, it is flagged for human review.</p>
      </>
    )
  }
];

const BlogPost = ({ post, onBack }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-3xl mx-auto pt-32 pb-20 px-6"
    >
      {/* Back Button */}
      <button 
        onClick={onBack}
        className="group flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors mb-8"
      >
        <div className="p-2 rounded-full bg-zinc-100 group-hover:bg-zinc-200 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </div>
        <span className="font-medium">Back to Blog</span>
      </button>

      {/* Header */}
      <header className="mb-10 text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="px-3 py-1 bg-zinc-100 text-zinc-900 rounded-full text-xs font-bold uppercase tracking-wider">
            {post.category}
          </span>
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-zinc-900 mb-6 leading-tight">
          {post.title}
        </h1>
        <div className="flex items-center justify-center gap-6 text-zinc-500 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {post.date}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {post.readTime}
          </div>
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {post.image && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
      )}

      {/* Content */}
      <div className="prose prose-lg prose-zinc mx-auto">
        {post.content}
      </div>

    </motion.article>
  );
};

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'AI & Education', 'Best Practices', 'Case Studies', 'Technology', 'Success Stories', 'Product Update'];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory || post.category + 's' === selectedCategory);

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-zinc-900 selection:text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-zinc-100 py-4">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white border-2 border-black rounded-sm flex items-center justify-center">
              <Check className="w-4 h-4 text-black" strokeWidth={3} />
            </div>
            <span className="font-bold text-lg tracking-tight text-zinc-900">GradeMind.ai</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <AnimatePresence mode="wait">
        {selectedPost ? (
          <BlogPost 
            key="post" 
            post={selectedPost} 
            onBack={() => setSelectedPost(null)} 
          />
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Hero Section */}
            <section className="pt-32 pb-12 px-6">
              <div className="max-w-4xl mx-auto text-center">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-900 mb-6"
                >
                  GradeMind Blog
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-xl text-zinc-600 leading-relaxed mb-10"
                >
                  Insights, stories, and best practices for modern educators leveraging AI in the classroom.
                </motion.p>
              </div>
            </section>

            {/* Filter Categories - Moved to Top */}
            <section className="pb-12 px-6 sticky top-20 z-40 bg-white/50 backdrop-blur-sm py-4 border-b border-zinc-100/50 mb-8">
               <div className="max-w-6xl mx-auto overflow-x-auto pb-2">
                <div className="flex flex-nowrap md:flex-wrap justify-center gap-3 min-w-max px-4">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-5 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                        selectedCategory === category
                          ? 'bg-zinc-900 text-white border-2 border-zinc-900'
                          : 'bg-white text-zinc-600 border-2 border-zinc-200 hover:border-zinc-400 hover:text-zinc-900'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="pb-20 px-6">
              <div className="max-w-6xl mx-auto">
                <AnimatePresence mode="popLayout">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredPosts.length > 0 ? (
                      filteredPosts.map((post, index) => (
                        <motion.article
                          key={post.id}
                          layout
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.3 }}
                          whileHover={{ y: -5 }}
                          onClick={() => setSelectedPost(post)}
                          className="bg-white border-2 border-zinc-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all cursor-pointer group flex flex-col h-full"
                        >
                          <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
                            <span className="px-3 py-1 bg-zinc-100 rounded-full text-xs font-medium text-zinc-900">
                              {post.category}
                            </span>
                          </div>

                          <h2 className="text-2xl font-bold text-zinc-900 mb-4 group-hover:text-zinc-700 transition-colors line-clamp-2">
                            {post.title}
                          </h2>

                          <p className="text-zinc-600 leading-relaxed mb-6 flex-1 line-clamp-3">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between text-sm text-zinc-500 mt-auto pt-6 border-t border-zinc-100">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{post.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>
                        </motion.article>
                      ))
                    ) : (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="col-span-full text-center py-20 text-zinc-500"
                      >
                        No posts found for this category.
                      </motion.div>
                    )}
                  </div>
                </AnimatePresence>
              </div>
            </section>

          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Blog;
